import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [scrollDirection, setScrollDirection] = useState('up');
  const [isAtTop, setIsAtTop] = useState(true);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  // Handle scroll events to show/hide header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // Determine if at top of page
      setIsAtTop(currentScrollY < 10);
      // Determine scroll direction
      const direction = currentScrollY > scrollY ? 'down' : 'up';
      setScrollDirection(direction);
      // Always show header at the top of the page
      if (currentScrollY < 10) {
        setIsVisible(true);
      }
      // Show header when scrolling up
      else if (direction === 'up') {
        setIsVisible(true);
      }
      // Hide header when scrolling down significantly
      else if (direction === 'down' && currentScrollY > 100) {
        setIsVisible(false);
      }
      setScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollY]);
  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);
  const toggleServicesDropdown = () => {
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };
  return <motion.header initial={{
    y: -100,
    opacity: 0
  }} animate={{
    y: isVisible ? 0 : -100,
    opacity: isVisible ? 1 : 0
  }} transition={{
    duration: 0.3,
    ease: 'easeInOut'
  }} className={`w-full fixed top-0 z-50 ${isHomePage && isAtTop ? 'bg-transparent' : 'bg-[#3a4a56]'} transition-colors duration-300`} data-id="element-438">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-439">
        <div className="flex justify-between items-center py-6" data-id="element-440">
          <motion.div className="flex items-center" whileHover={{
          scale: 1.05
        }} transition={{
          type: 'spring',
          stiffness: 400
        }} data-id="element-441">
            <Link to="/" className="text-2xl font-normal text-white logo-text" data-id="element-442">
              Immovendu
            </Link>
          </motion.div>
          {/* Desktop navigation */}
          <nav className="hidden md:flex space-x-8" data-id="element-443">
            <motion.div whileHover={{
            y: -2
          }} transition={{
            type: 'spring',
            stiffness: 500
          }} data-id="element-444">
              <Link to="/" className="text-white hover:text-[#f2f2ed] font-normal" data-id="element-445">
                Home
              </Link>
            </motion.div>
            <motion.div whileHover={{
            y: -2
          }} transition={{
            type: 'spring',
            stiffness: 500
          }} data-id="element-446">
              <Link to="/about" className="text-white hover:text-[#f2f2ed] font-normal" data-id="element-447">
                About
              </Link>
            </motion.div>
            <motion.div whileHover={{
            y: -2
          }} transition={{
            type: 'spring',
            stiffness: 500
          }} data-id="element-448">
              <a href="https://www.realtor.ca/map#IndividualId=2218124" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#f2f2ed] font-normal" data-id="element-449">
                Listings
              </a>
            </motion.div>
            <div className="relative" ref={dropdownRef} data-id="element-450">
              <motion.button className="text-white hover:text-[#f2f2ed] font-normal flex items-center" onClick={toggleServicesDropdown} whileHover={{
              y: -2
            }} transition={{
              type: 'spring',
              stiffness: 500
            }} data-id="element-451">
                Services
                <motion.span animate={{
                rotate: isServicesDropdownOpen ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-452">
                  <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-453" />
                </motion.span>
              </motion.button>
              <AnimatePresence data-id="element-454">
                {isServicesDropdownOpen && <motion.div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10" initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} data-id="element-455">
                    <motion.div whileHover={{
                  x: 5
                }} transition={{
                  type: 'spring',
                  stiffness: 500
                }} data-id="element-456">
                      <Link to="/services/buyers" className="block px-4 py-2 text-sm text-[#3a4a56] hover:bg-[#f2f2ed]" data-id="element-457">
                        Buyers
                      </Link>
                    </motion.div>
                    <motion.div whileHover={{
                  x: 5
                }} transition={{
                  type: 'spring',
                  stiffness: 500
                }} data-id="element-458">
                      <Link to="/services/sellers" className="block px-4 py-2 text-sm text-[#3a4a56] hover:bg-[#f2f2ed]" data-id="element-459">
                        Sellers
                      </Link>
                    </motion.div>
                    <motion.div whileHover={{
                  x: 5
                }} transition={{
                  type: 'spring',
                  stiffness: 500
                }} data-id="element-460">
                      <Link to="/services/marketing" className="block px-4 py-2 text-sm text-[#3a4a56] hover:bg-[#f2f2ed]" data-id="element-461">
                        Marketing
                      </Link>
                    </motion.div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <motion.div whileHover={{
            y: -2
          }} transition={{
            type: 'spring',
            stiffness: 500
          }} data-id="element-462">
              <Link to="/contact" className="text-white hover:text-[#f2f2ed] font-normal" data-id="element-463">
                Contact
              </Link>
            </motion.div>
          </nav>
          {/* Mobile menu button */}
          <div className="md:hidden" data-id="element-464">
            <motion.button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white hover:text-[#f2f2ed] focus:outline-none" whileTap={{
            scale: 0.95
          }} data-id="element-465">
              {isMenuOpen ? <XIcon className="h-6 w-6" data-id="element-466" /> : <MenuIcon className="h-6 w-6" data-id="element-467" />}
            </motion.button>
          </div>
        </div>
        {/* Mobile menu */}
        <AnimatePresence data-id="element-468">
          {isMenuOpen && <motion.div className="md:hidden bg-[#3a4a56] bg-opacity-95 rounded-lg" initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} data-id="element-469">
              <div className="px-2 pt-2 pb-4 space-y-1 sm:px-3" data-id="element-470">
                <Link to="/" className="block px-3 py-2 text-white hover:text-[#f2f2ed] font-normal" data-id="element-471">
                  Home
                </Link>
                <Link to="/about" className="block px-3 py-2 text-white hover:text-[#f2f2ed] font-normal" data-id="element-472">
                  About
                </Link>
                <a href="https://www.realtor.ca/map#IndividualId=2218124" target="_blank" rel="noopener noreferrer" className="block px-3 py-2 text-white hover:text-[#f2f2ed] font-normal" data-id="element-473">
                  Listings
                </a>
                <div data-id="element-474">
                  <button onClick={toggleServicesDropdown} className="flex justify-between items-center w-full px-3 py-2 text-white hover:text-[#f2f2ed] font-normal" data-id="element-475">
                    Services
                    <motion.span animate={{
                  rotate: isServicesDropdownOpen ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-476">
                      <ChevronDownIcon className="ml-1 h-4 w-4" data-id="element-477" />
                    </motion.span>
                  </button>
                  <AnimatePresence data-id="element-478">
                    {isServicesDropdownOpen && <motion.div className="pl-4 space-y-1" initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-479">
                        <Link to="/services/buyers" className="block px-3 py-2 text-white hover:text-[#f2f2ed] font-normal" data-id="element-480">
                          Buyers
                        </Link>
                        <Link to="/services/sellers" className="block px-3 py-2 text-white hover:text-[#f2f2ed] font-normal" data-id="element-481">
                          Sellers
                        </Link>
                        <Link to="/services/marketing" className="block px-3 py-2 text-white hover:text-[#f2f2ed] font-normal" data-id="element-482">
                          Marketing
                        </Link>
                      </motion.div>}
                  </AnimatePresence>
                </div>
                <Link to="/contact" className="block px-3 py-2 text-white hover:text-[#f2f2ed] font-normal" data-id="element-483">
                  Contact
                </Link>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.header>;
};